import React, { useEffect, useState } from "react";
import { COMPANION } from "../config/app";
import { detectPlatform } from "../utils/platform";

/**
 * Store links, ordered by detected platform.
 *
 * These are plain styled buttons rather than Apple's and Google's official
 * badge images, for two reasons. Both badges are trademarked artwork with
 * strict usage rules about size, spacing and alteration, and getting them
 * subtly wrong is a real (if small) legal exposure. And hotlinking them
 * from Apple's or Google's CDN would make a third-party request on every
 * page view, which would quietly contradict the promise on the privacy
 * page that this site has no third-party embeds.
 *
 * If you want the official badges, download them from each vendor's brand
 * portal and serve them from /public — same visual result, promise intact.
 */
export default function StoreLinks({ compact = false }) {
  // "unknown" until an effect runs — see utils/platform.js on why detection
  // can't happen during render on a prerendered page.
  const [platform, setPlatform] = useState("unknown");
  useEffect(() => setPlatform(detectPlatform()), []);

  // The app isn't out yet: show a clear "coming soon" state instead of
  // buttons that link nowhere. Flip COMPANION.comingSoon to false and fill
  // in the store URLs to restore real buttons.
  if (COMPANION.comingSoon) {
    return (
      <div className={`store-links ${compact ? "store-links-compact" : ""}`}>
        <div className="store-soon" role="note">
          <span className="store-soon-badge">Coming soon</span>
          <span className="store-soon-text">
            The Filtspot app for iPhone and Android is on the way.
          </span>
        </div>
      </div>
    );
  }

  const apple = (
    <a
      key="ios"
      className="store-link"
      href={COMPANION.appStoreUrl}
      target="_blank"
      rel="noopener noreferrer"
    >
      <span className="store-eyebrow">Download on the</span>
      <span className="store-name">App Store</span>
    </a>
  );

  const google = (
    <a
      key="android"
      className="store-link"
      href={COMPANION.playStoreUrl}
      target="_blank"
      rel="noopener noreferrer"
    >
      <span className="store-eyebrow">Get it on</span>
      <span className="store-name">Google Play</span>
    </a>
  );

  // Detected platform first, the other still present as a smaller link —
  // never hidden, because detection is wrong often enough to matter.
  let primary = null;
  let secondary = null;
  if (platform === "ios") {
    primary = apple;
    secondary = google;
  } else if (platform === "android") {
    primary = google;
    secondary = apple;
  }

  if (!primary) {
    return (
      <div className={`store-links ${compact ? "store-links-compact" : ""}`}>
        {apple}
        {google}
      </div>
    );
  }

  return (
    <div className={`store-links ${compact ? "store-links-compact" : ""}`}>
      {primary}
      <a
        className="store-alt"
        href={secondary.props.href}
        target="_blank"
        rel="noopener noreferrer"
      >
        Also on {secondary.key === "ios" ? "the App Store" : "Google Play"}
      </a>
    </div>
  );
}
